import React from 'react';
import MuiAccordion from '@mui/material/Accordion';
import MuiAccordionSummary from '@mui/material/AccordionSummary';
import MuiAccordionDetails from '@mui/material/AccordionDetails';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';

import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

import { MembershipProductServices } from '../../api/model/sponsorship';
import { Color } from '../../theme/colors';

type ProductAccordionProps = {
  icon: React.ElementType;
  productName: string;
  isIncluded: boolean;
  services?: MembershipProductServices[];
  isDarkMode?: boolean;
};

export function ProductAccordion({
  icon: Icon,
  productName,
  isIncluded,
  services,
  isDarkMode = false,
}: ProductAccordionProps): React.JSX.Element {
  if (!isIncluded) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          padding: '4px 8px',
          borderRadius: '6px',
          backgroundColor: isDarkMode ? Color.Raspberry100 : Color.Raspberry600,
          gap: '6px',
        }}
      >
        <CancelOutlinedIcon sx={{ fontSize: '16px', color: isDarkMode ? Color.BaseWhite : Color.Raspberry50 }} />
        <Typography variant='caption' sx={{ color: isDarkMode ? Color.BaseWhite : Color.Raspberry50 }}>
          {productName}
        </Typography>
      </Box>
    );
  }

  return (
    <MuiAccordion disableGutters elevation={0} sx={{ background: 'transparent', '&::before': { display: 'none' } }}>
      <MuiAccordionSummary
        expandIcon={
          <ExpandMoreIcon sx={{ fontSize: '14px', color: isDarkMode ? Color.BaseWhite : Color.SeaGreen50 }} />
        }
        sx={{
          backgroundColor: isDarkMode ? Color.SeaGreen100 : Color.SeaGreen600,
          borderRadius: '6px !important',
          minHeight: 'unset',
          padding: '4px 8px',
          '& .MuiAccordionSummary-content': { margin: '0', alignItems: 'center', gap: '10px' },
          '&.Mui-expanded': { minHeight: 'unset' },
          '& .MuiAccordionSummary-content.Mui-expanded': { margin: '0' },
        }}
      >
        <Icon sx={{ fontSize: '14px', color: isDarkMode ? Color.BaseWhite : Color.SeaGreen50 }} />
        <Typography variant='caption' sx={{ color: isDarkMode ? Color.BaseWhite : Color.SeaGreen50 }}>
          {productName}
        </Typography>
      </MuiAccordionSummary>
      {services && services.length > 0 && (
        <MuiAccordionDetails
          sx={{ padding: '10px 8px 12px 8px', backgroundColor: isDarkMode ? '#4F4898' : Color.BaseWhite }}
        >
          {services.map((service) => (
            <Box key={service.name} sx={{ display: 'flex', alignItems: 'center', gap: '6px', paddingY: '2px' }}>
              {service.included ? (
                <CheckIcon sx={{ fontSize: '16px', color: isDarkMode ? Color.BaseWhite : Color.Slate100 }} />
              ) : (
                <CloseIcon sx={{ fontSize: '16px', color: isDarkMode ? Color.BaseWhite : Color.Slate100 }} />
              )}
              <Typography variant='caption' sx={{ color: isDarkMode ? Color.BaseWhite : Color.Slate100 }}>
                {service.name}
              </Typography>
            </Box>
          ))}
        </MuiAccordionDetails>
      )}
    </MuiAccordion>
  );
}

export default ProductAccordion;
